/**
 * The documents the rules builder reads for one plan: provider manuals and
 * policy letters, one URL per program. The builder fetches each enabled source
 * on its schedule (or when run), drafts from it only when the text changed,
 * and records the last fetch result shown here. A source that keeps failing
 * gets a new URL here (the harness's Fix opens this editor on it).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { funderAgentsApi, PROGRAM_TYPES, type FunderSource, type ProgramType } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, errText, fmtDate, type Notify } from './shared';

const STATUS_TEXT: Record<string, string> = {
  ok: 'Fetched',
  unchanged: 'Unchanged since last draft',
  unreachable: 'Unreachable',
  too_large: 'Too large to read',
  unsupported: 'Format not supported',
  error: 'Error',
};

export default function SourcesEditor({
  payerId,
  onChanged,
  notify,
  focusSourceId,
}: {
  payerId: number;
  onChanged?: () => void;
  notify: Notify;
  /** Open this source's URL for editing (the harness found it unreachable). */
  focusSourceId?: number | null;
}) {
  const [sources, setSources] = useState<FunderSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [programType, setProgramType] = useState<ProgramType>('ECM');
  const [sourceUrl, setSourceUrl] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(focusSourceId ?? null);
  const [editUrl, setEditUrl] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      setSources(await funderAgentsApi.sources(payerId));
    } catch (e) {
      setError(errText(e));
      setSources([]);
    }
  }, [payerId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (focusSourceId) setEditing(focusSourceId);
  }, [focusSourceId]);
  useEffect(() => {
    const s = (sources ?? []).find((x) => x.id === editing);
    if (s) setEditUrl(s.source_url);
  }, [editing, sources]);

  const urlOk = /^https:\/\/\S+$/i.test(sourceUrl.trim());

  const add = async () => {
    if (!urlOk) return;
    setBusy('add');
    try {
      await funderAgentsApi.addSource({ payer_id: payerId, program_type: programType, source_url: sourceUrl.trim(), label: label.trim() || undefined });
      setSourceUrl('');
      setLabel('');
      notify(true, 'Source added. The builder reads it on its next run for this plan.');
      await load();
      onChanged?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (s: FunderSource) => {
    setBusy(`t${s.id}`);
    try {
      await funderAgentsApi.updateSource(s.id, { enabled: !s.enabled });
      await load();
      onChanged?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const saveUrl = async (s: FunderSource) => {
    const next = editUrl.trim();
    if (!/^https:\/\/\S+$/i.test(next) || next === s.source_url) return;
    setBusy(`u${s.id}`);
    try {
      await funderAgentsApi.updateSource(s.id, { source_url: next });
      setEditing(null);
      notify(true, 'Source URL saved. The builder reads it on its next run for this plan.');
      await load();
      onChanged?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s: FunderSource) => {
    if (!window.confirm(`Remove this source?\n\n${s.source_url}\n\nRules already approved from it stay in place.`)) return;
    setBusy(`d${s.id}`);
    try {
      await funderAgentsApi.deleteSource(s.id);
      await load();
      onChanged?.();
    } catch (e) {
      notify(false, errText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <ErrorLine text={error} />
      {sources === null ? (
        <p className="text-sm text-ink-500">Loading sources…</p>
      ) : sources.length === 0 ? (
        <Empty>Add a source URL for this plan — its provider manual or a policy letter — then draft rules.</Empty>
      ) : (
        <ul className="divide-y divide-v2border rounded-md border border-v2border bg-surface">
          {sources.map((s) => (
            <li key={s.id} className={`flex flex-wrap items-center gap-3 px-3 py-2 text-sm ${s.id === focusSourceId ? 'bg-attention-soft' : ''}`}>
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-paper-2 text-ink-700">{s.program_type}</span>
              <a href={s.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-v2accent-ink hover:underline break-all">
                {s.label || s.source_url}
                <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
              </a>
              <span className="text-xs text-ink-500">
                {s.last_status ? STATUS_TEXT[s.last_status] ?? s.last_status : 'Not fetched yet'}
                {s.last_fetched_at ? ` · ${fmtDate(s.last_fetched_at)}` : ''}
              </span>
              {(s.consecutive_failures ?? 0) > 0 && (
                <span className="text-xs font-semibold text-danger-ink">
                  failed {s.consecutive_failures} time{s.consecutive_failures === 1 ? '' : 's'} in a row
                </span>
              )}
              {s.last_error && <span className="text-xs text-danger-ink">{s.last_error}</span>}
              <span className="ml-auto flex items-center gap-2">
                <Btn tone="ghost" title="Change URL" onClick={() => setEditing(editing === s.id ? null : s.id)}>
                  <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                </Btn>
                <label className="inline-flex items-center gap-1 text-xs text-ink-700">
                  <input type="checkbox" checked={s.enabled} disabled={busy === `t${s.id}`} onChange={() => void toggle(s)} />
                  Enabled
                </label>
                <Btn tone="ghost" title="Remove source" busy={busy === `d${s.id}`} onClick={() => void remove(s)}>
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                </Btn>
              </span>
              {editing === s.id && (
                <div className="flex w-full flex-wrap items-end gap-2">
                  <label className="flex-1 min-w-[240px] text-xs font-semibold text-ink-700">
                    New URL (https)
                    <input
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
                    />
                  </label>
                  <Btn
                    tone="primary"
                    disabled={!/^https:\/\/\S+$/i.test(editUrl.trim()) || editUrl.trim() === s.source_url}
                    busy={busy === `u${s.id}`}
                    onClick={() => void saveUrl(s)}
                  >
                    Save URL
                  </Btn>
                  <Btn tone="ghost" onClick={() => setEditing(null)}>
                    Cancel
                  </Btn>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-semibold text-ink-700">
          Program
          <select
            value={programType}
            onChange={(e) => setProgramType(e.target.value as ProgramType)}
            className="mt-1 block rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
          >
            {PROGRAM_TYPES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold text-ink-700 flex-1 min-w-[240px]">
          Source URL (https)
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://plan.example/provider-manual.pdf"
            className="mt-1 block w-full rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
          />
        </label>
        <label className="text-xs font-semibold text-ink-700">
          Label (optional)
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Provider manual, ch. 4"
            className="mt-1 block rounded-md border border-v2border bg-surface px-2 py-1.5 text-sm text-ink"
          />
        </label>
        <Btn tone="primary" disabled={!urlOk} busy={busy === 'add'} onClick={() => void add()}>
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          Add source
        </Btn>
      </div>
    </div>
  );
}
